/* eslint-disable prettier/prettier */
/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';
import merge from 'lodash/merge';
import Image from 'next/image';
import { useContext, useRef, useState } from 'react';

import useTranslate from '@common/hooks/useTranslate';
import { formatPrices } from '@common/utlis';
import AddToCartIconButton from '@stories/atoms/AddToCartIconButton';
import BadgeVoucher from '@stories/atoms/BadgeVoucher';
import { RatingStarsCompact } from '@stories/atoms/RatingStars';
import BadgesBar from '@stories/organisms/HitListView/BadgesBar';
import { PageContext } from '@stories/templates/Context/pageContext';
import { media } from '@stories/theming/settings';
import { ProductWithOptionsContextProvider } from '@stories/utlis/context/ProductWithOptionsContext';
import { useProductListContext } from '@utils/productList';

import { useIntersection } from '../../../common/hooks/useIntersection';
import { trackGAProductClick, trackGAProductImpression } from '../../../common/tracking';

import Headline from './Headline';
import Price from './Price';

import type { PriceProps } from './Price';
import type { ProductType } from './ReduceProps';
import type { CssProps } from '@stories/@types/custom';
import type { TBadges } from '@stories/organisms/HitListView/BadgesBar';
import type { TProductOptions } from '@stories/utlis/context/ProductWithOptionsContext';

const translations = {
  'See product': { de_DE: 'Zum Produkt', nl_NL: 'Weergeven' },
  'Out of stock': { de_DE: 'Derzeit ausverkauft', nl_NL: 'Uitverkocht' }
};

const imageSizes = '(min-width: 1024px) 308px, (min-width: 768px) 350px, calc(100vw - 132px)';

export type Voucher = {
  discount: number;
  voucherCode: string;
};

export type THitListViewProps = {
  badges?: TBadges;
  brand?: string | null;
  debug?: object;
  figure: {
    alt: string;
    image: {
      path: string;
    };
  };
  id: number;
  inStock: boolean;
  name: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  prices: PriceProps;
  productOptions?: TProductOptions | null;
  productType?: ProductType | null;
  shortDescription: string;
  sku?: string | null;
  url: string;
  reviews: {
    totalReviews: number;
    rating: number;
  };
  voucher?: Voucher | null;
} & CssProps;

const containerClass = {
  alignContent: 'start',
  display: 'grid',
  gridColumnGap: '1.25rem',
  gridTemplateAreas: `
    'headline headline'
    'figure   info'
    'promo    promo'
    'usp      usp'`,
  gridTemplateColumns: '1fr 120px',
  paddingTop: 0,
  paddingBottom: 0,
  position: 'relative',

  [media('tablet')]: {
    ':nth-of-type(1)': {
      marginTop: '1.25rem !important',
      paddingTop: 0
    },
    ':nth-of-type(2)': {
      marginTop: '1.25rem',
      paddingTop: 0
    },
    ':nth-last-child(2)': {
      '::after': {
        content: 'none'
      }
    }
  }
} as const;

const infoClass = {
  '> *:not(:last-child)': {
    marginBottom: '2.5rem'
  }
} as const;

const uspClass = () =>
  ({
    gridArea: 'usp',
    paddingTop: '.75rem',

    ul: {
      paddingLeft: 0,

      '~ p': {
        // fix bodylab extra data in USP
        display: 'none'
      }
    },

    li: {
      WebkitLineClamp: '2',
      WebkitBoxOrient: 'vertical',
      display: '-webkit-box',
      lineHeight: '125%',
      marginTop: '7px',
      overflow: 'hidden',
      paddingLeft: '1rem',
      position: 'relative',

      '::before': {
        aspectRatio: '1',
        backgroundColor: 'hsl(0 0% 40%)',
        borderRadius: '50%',
        content: "''",
        left: '2px',
        position: 'absolute',
        top: '6px',
        width: '6px'
      }
    }
  }) as const;

const headlineClass = {
  gridArea: 'headline',
  marginBottom: '.75rem',

  ':hover': {
    textDecoration: 'none'
  }
} as const;

const themes = {
  vomachterhof: {
    headlineClass: {
      marginBottom: '1.25rem'
    }
  }
};

const DebugOverlay = ({ debug }) => {
  const [showAll, setShowAll] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const theme = useTheme();

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 5000);
  };

  return (
    <div
      css={{
        backdropFilter: `blur(${showAll ? '25px' : '2px'})`,
        background: 'rgba(0,0,0,0.65)',
        borderRadius: '4px',
        color: 'white',
        padding: '10px',
        position: 'absolute',
        width: 'calc(100% - 2rem)',
        maxHeight: '100%',
        zIndex: 100,
        boxSizing: 'border-box',
        display: 'inline-flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'backdrop-filter 0.3s ease',
        margin: '0 1rem'
      }}
    >
      <div
        css={{
          overflow: 'auto',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          flex: '1 1 auto',
          marginBottom: '2.5rem'
        }}
      >
        {(showAll ? Object.keys(debug) : Object.keys(debug).slice(0, 3)).map((key) => (
          <p
            key={key}
            css={{
              fontSize: showAll ? theme.font.size.small : 'inherit',
              marginBottom: '.5rem',
              lineHeight: '120%',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              columnGap: '1rem',
              rowGap: '.5rem'
            }}
          >
            <div css={{ maxWidth: '100%' }}>
              <strong>{`${key}:`}</strong> {debug[key]}
            </div>
            <button
              onClick={() => copyToClipboard(debug[key], key)}
              css={{
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
                fontSize: theme.font.size.tiny,
                lineHeight: '140%',
                padding: '0 .5rem'
              }}
            >
              {copiedKey === key ? 'Copied!' : 'Copy'}
            </button>
          </p>
        ))}
      </div>
      <button
        onClick={() => setShowAll(!showAll)}
        css={{
          fontSize: theme.font.size.small,
          position: 'absolute',
          bottom: '1rem',
          right: '10px',
          background: 'rgba(255, 255, 255, 0.3)',
          borderRadius: '4px',
          padding: '0 .5rem'
        }}
      >
        {showAll ? 'Read Less' : 'Read More'}
      </button>
    </div>
  );
};

const HitListView: React.FC<THitListViewProps> = ({
  badges,
  brand,
  cssProps,
  debug,
  figure,
  id,
  inStock,
  name,
  prices,
  productOptions = null,
  productType,
  reviews,
  shortDescription,
  url,
  voucher
}) => {
  const { storeCode } = useContext(PageContext);
  const { currentCategoryId } = useProductListContext();
  const position = 0;

  const { price, priceValue, base: baseprice, discount } = formatPrices(prices);
  const hitRef = useRef<HTMLElement>(null);
  const translate = useTranslate(translations);
  const trackingData = {
    brand: brand ?? '',
    module: currentCategoryId ? `Hit List View - Category - ${currentCategoryId}` : 'Hit List View - Search',
    id: id.toString(),
    name,
    position,
    price: priceValue
  };

  useIntersection(hitRef, () => trackGAProductImpression(trackingData));

  return (
    <ProductWithOptionsContextProvider
      {...{
        productId: id.toString(),
        productImage: figure.image.path,
        name,
        price,
        baseprice,
        productOptions,
        productType
      }}
    >
      <article
        className="hit-list-view"
        ref={hitRef}
        css={merge(
          {},
          {
            ...containerClass,
            ...(!inStock && { opacity: 0.6 })
          },
          cssProps
        )}
      >
        {debug && <DebugOverlay debug={debug} />}
        <div css={{ ...headlineClass, ...themes[storeCode]?.headlineClass }}>
          {typeof badges !== 'undefined' && (
            <BadgesBar badges={badges} cssProps={{ marginBottom: '.25rem' }} />
          )}

          <Headline {...{ brand, id, name, price, url }} onClick={() => trackGAProductClick(trackingData)} />
          {!inStock && (
            <small
              css={(t) => ({
                color: t.color.promotion,
                display: 'block',
                flex: '100% 0 0',
                fontSize: '1rem',
                fontWeight: 'bold',
                margin: '0.5rem 0',
                // position: 'absolute',
                zIndex: 5
              })}
            >
              {translate('Out of stock')}
            </small>
          )}
        </div>
        <div
          css={{
            position: 'relative',
            width: '100%'
          }}
        >
          <div
            css={{
              aspectRatio: '1',
              position: 'relative',
              width: '100%'
            }}
          >
            <Image
              sizes={imageSizes}
              src={figure.image.path}
              alt={figure.alt}
              fill
              style={{ objectFit: 'contain' }}
            />
          </div>
        </div>
        <div css={infoClass}>
          <Price {...prices} />
          {reviews.totalReviews > 0 && <RatingStarsCompact ratingAverage={reviews.rating} {...reviews} />}
          {inStock && <AddToCartIconButton productId={id.toString()} />}
        </div>
        <div css={uspClass}>
          {productOptions?.[0] &&
            productOptions[0]?.selection?.filter((product) => product.is_in_stock).length > 1 && (
              <div
                id="productTastesAB"
                css={(t) => ({
                  backgroundColor: t.color.primary,
                  color: t.color.white,
                  borderRadius: '8px',
                  padding: '0 .5rem',
                  marginBottom: '.5rem',
                  display: 'inline-block',
                  fontWeight: 'bold'
                })}
              >{`${productOptions[0]?.selection?.filter((product) => product.is_in_stock).length} Geschmacksrichtungen`}</div>
            )}
          {voucher && (
            <BadgeVoucher
              {...voucher}
              productAlreadyDiscounted={typeof discount === 'number' && discount !== 0}
            />
          )}
          <div dangerouslySetInnerHTML={{ __html: shortDescription }} />
        </div>
      </article>
    </ProductWithOptionsContextProvider>
  );
};

export default HitListView;
